import { useSelector } from 'react-redux';
import { LayoutType, WaveType } from 'signup-components/types';
import { Step } from 'signup-constants/Step';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { LoadingKey } from '../app-active-loading/LoadingKey';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { useMessage } from '../messaging/useMessage';
import { getActiveError } from '../store/error/selectors';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { getActiveLoading } from '../store/loading-status/selectors';
import { getStepConfig } from '../views/getStepConfig';
import { useCurrentStep } from './useCurrentStep';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { getDecorativeBgComponent } from '../experiments/acq0143/ACQ143Utils';
export const getWaveType = ({
  currentStep,
  postAccountCreationSteps,
  activeLoading,
  stepConfig
}) => {
  const isPostAccountCreationStep = postAccountCreationSteps.includes(currentStep);
  if (activeLoading || !stepConfig) {
    return undefined;
  }
  if (isPostAccountCreationStep && getFlowConfig().shouldUseWaveOnPostAccountCreation || currentStep === Step.InstallSalesExtension) {
    return WaveType.LowOrange;
  }
  return stepConfig.waveType;
};
export const useLayoutTemplate = () => {
  const postAccountCreationSteps = useSelector(getPostAccountCreationSteps);
  const {
    currentStep
  } = useCurrentStep();
  const activeError = useSelector(getActiveError);
  const step = activeError || currentStep;
  const stepConfig = getStepConfig(step);
  const {
    isActive: isInstanceOfSignupEmbedder
  } = useSignupEmbedder();
  const showMessagingAboveContent = stepConfig && stepConfig.showMessagingAboveContent;
  const {
    shouldDisplayMessaging
  } = useMessage();
  const {
    isAssignedToVariant: isACQ0143Variant
  } = useExperiment(ExperimentKeys.ACQ0143, TrackWhen.never);
  const {
    isAssignedToVariant: isACQ0145Variant
  } = useExperiment(ExperimentKeys.ACQ0145, TrackWhen.never);
  const layoutType = shouldDisplayMessaging && !showMessagingAboveContent ? LayoutType.TwoColumn : getFlowConfig().getLayoutType();
  const activeLoading = useSelector(getActiveLoading);
  const waveType = getWaveType({
    currentStep,
    postAccountCreationSteps,
    activeLoading,
    stepConfig
  });
  const isPostAccountCreationStep = postAccountCreationSteps.includes(currentStep);
  const isNotVerificationLoading = activeLoading !== LoadingKey.InitiateVerificationRequest || isACQ0143Variant();
  const stepConfigHasProgressBar = stepConfig && stepConfig.stepKey && !stepConfig.hideProgressBar || (isACQ0143Variant() || isACQ0145Variant()) && activeLoading && activeLoading !== LoadingKey.BlankScreenLoading;
  const shouldShowProgressBar = isNotVerificationLoading && !activeError && stepConfigHasProgressBar;
  if (activeLoading) {
    return {
      layoutType: LayoutType.CentredStep,
      waveType,
      shouldShowProgressBar
    };
  }
  if (isACQ0143Variant() && ![Step.ExistingAccounts, Step.ExistingUser, Step.LandingPage].includes(currentStep) && !activeError) {
    const layoutTypeACQ0143 = shouldDisplayMessaging ? LayoutType.LeftAlignedTwoColumn : LayoutType.LeftAligned;
    const decorativeBgComponent = getDecorativeBgComponent({
      currentStep,
      activeError,
      activeLoading
    });
    return {
      layoutType: layoutTypeACQ0143,
      waveType,
      shouldShowProgressBar,
      decorativeBgComponent
    };
  }
  if (isACQ0145Variant() && isPostAccountCreationStep && currentStep === Step.UseCase && !activeError) {
    const decorativeBgComponent = getDecorativeBgComponent({
      currentStep,
      activeError,
      activeLoading
    });
    return {
      layoutType: LayoutType.LeftAligned,
      shouldShowProgressBar,
      decorativeBgComponent
    };
  }
  if (isPostAccountCreationStep && !isInstanceOfSignupEmbedder) {
    return {
      layoutType,
      waveType,
      shouldShowProgressBar
    };
  }
  return {
    layoutType,
    waveType,
    shouldShowProgressBar
  };
};